import { Link, useLoaderData, useSearchParams } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { useCurrentUser } from "~/api/queries/useCurrentUser";
import { useOpenPreinscriptionCourses } from "~/api/queries/useOpenPreinscriptionCourses";
import { studentCoursesQueryOptions } from "~/api/queries/useStudentCourses";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { ReserveInCartButton } from "~/modules/Cart/ReserveInCartButton";
import { useLanguageStore } from "~/modules/Dashboard/Settings/Language/LanguageStore";
import { buildMeta, getCompanyFromMatches } from "~/utils/meta-helpers";
import { hasSession, serverFetchSafe } from "~/utils/server-fetch.server";

import { CoursesFilterBar } from "../components/CoursesFilterBar";
import { PublicCourseCard } from "../components/PublicCourseCard";
import {
  applyFilters,
  CLIENT_FILTER_MAX,
  defaultOrder,
  deriveFacets,
  initialFilters,
  isFilterActive,
  resolveCategory,
} from "../lib/courseFacets";
import { leadSentences } from "../lib/excerpt";

import type { CourseFilters } from "../lib/courseFacets";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import type { SupportedLanguages } from "@repo/shared";
import type { GetAvailableCoursesResponse } from "~/api/generated-api";

/**
 * Loader de SERVIDOR: siembra el catálogo para que las tarjetas salgan EN EL HTML.
 *
 * Antes esta página SSR-eaba sólo el cromo (título, filtros) y las tarjetas eran skeletons,
 * porque el único fetch era client-side. Un crawler de LLM no ejecuta JS: veía un catálogo
 * sin cursos.
 *
 * Los parámetros son los MISMOS que `availableCoursesQueryOptions` (page 1, perPage 100,
 * sin filtros): eso es lo que hace que el HTML servido y el primer render del cliente
 * pinten la misma lista y no haya mismatch de hidratación. El filtrado sigue siendo 100%
 * en cliente (ver el comentario de `lib/courseFacets.ts`), así que no hay que replicarlo acá.
 *
 * El fetch es ANÓNIMO a propósito: es lo que ve un bot, y es lo que se puede cachear por URL.
 * El estado por-usuario (enrolled/purchased) lo sigue resolviendo React Query al hidratar.
 */
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const response = await serverFetchSafe<GetAvailableCoursesResponse>(
    "/api/course/available-courses?page=1&perPage=100&language=es",
  );

  // `authenticated` viaja hasta el componente para que un visitante con sesión no vea los
  // botones del deslogueado. Ver `hasSession` en server-fetch.server.ts.
  return { courses: response?.data ?? null, authenticated: hasSession(request) };
};

type FilterAction =
  | { type: "SET_SEARCH_QUERY"; payload: string }
  | { type: "SET_CATEGORY"; payload: string }
  | { type: "SET_PRICE_FILTER"; payload: CourseFilters["priceFilter"] }
  | { type: "CLEAR_FILTERS" };

export const meta: MetaFunction = ({ matches }) => {
  const company = getCompanyFromMatches(matches);
  return buildMeta({ title: `Cursos | ${company}` });
};

function filterReducer(state: CourseFilters, action: FilterAction): CourseFilters {
  switch (action.type) {
    case "SET_SEARCH_QUERY":
      return { ...state, searchQuery: action.payload };
    case "SET_CATEGORY":
      return { ...state, category: action.payload };
    case "SET_PRICE_FILTER":
      return { ...state, priceFilter: action.payload };
    case "CLEAR_FILTERS":
      return initialFilters;
    default:
      return state;
  }
}

/**
 * Catálogo público de cursos.
 *
 * El problema que resuelve este layout: en mobile la primera tarjeta arrancaba a 651px de scroll
 * (390×844) y a 679px (360×740) — 77% y 92% de la primera pantalla gastados en cromo antes de que
 * asome lo único que se vende. De esos 651px, 310 eran el bloque de filtros: cinco controles
 * apilados a ancho completo para filtrar DOS cursos publicados, de los cuales tres no podían
 * discriminar nada en ningún ambiente.
 *
 * Las cuatro decisiones que mueven los píxeles:
 * 1. Los controles los decide el dato (`deriveFacets`), no el diseño: lo que no puede discriminar
 *    nada no se renderiza.
 * 2. La barra de filtros mide 44px en mobile SIEMPRE, con cualquier cantidad de chips, porque es un
 *    riel horizontal de una sola fila y no un `flex-wrap` (ver `CoursesFilterBar`). Esto es lo que
 *    hace que el arreglo no se autodestruya cuando crezca el catálogo: la versión anterior medía
 *    44px con los datos de staging, 96px con los de prod y 252px con 9 cursos.
 * 3. El hero va alineado a la izquierda con el subtítulo largo escondido en mobile (`sm:hidden` /
 *    `hidden sm:block`, NUNCA render condicional por JS: esta página es SSR y participa del
 *    framework de LLM SEO, así que las dos versiones tienen que estar en el HTML).
 * 4. La tarjeta bajó de 524px a ~375px sin tocar el thumbnail (ver `PublicCourseCard`).
 *
 * 5. El fold NO depende de la query string. `?category=` se resuelve contra las facetas reales antes
 *    de filtrar nada (`resolveCategory`), así que un valor que no matchea no filtra en vez de vaciar
 *    la grilla. Era el bloqueante más caro de los tres: la primera tarjeta arrancaba a 469/496/537px
 *    con un link que la propia app emitía.
 *
 * Los escenarios de datos que hay que medir siempre juntos, porque cada uno prende controles
 * distintos (`apps/web/scripts/qa-courses-facets.mjs`): staging (2 gratis, 1 categoría → ningún
 * control), prod (1 gratis + 1 pago → 3 chips de precio), catálogo crecido (9 cursos, 2 categorías
 * → buscador + 6 chips) y el catálogo crecido entrando por `?category=` basura.
 */
export default function CoursesPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") ?? "";
  const [filters, dispatch] = useReducer(filterReducer, {
    ...initialFilters,
    category: initialCategory,
  });
  const { language } = useLanguageStore();

  // Fetch current user (null if not logged in)
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const isLoggedIn = !!currentUser;

  // UNA sola query, sin parámetros de filtro: cacheada, sin refetch al tipear ni al tocar un chip.
  // El filtrado se hace en cliente (ver el comentario largo en `lib/courseFacets.ts`) porque el
  // full-text del backend está roto — índice en 'english', query en 'spanish' — y buscar
  // "inteligencia" devuelve 0 cursos con el curso llamándose "Introducción a la Inteligencia
  // Artificial". El filtro de idioma se retiró entero: no hay un solo curso publicado en inglés.
  // El loader no reemplaza a React Query: la SIEMBRA. `ssrCourses` viaja serializado en
  // `window.__remixContext`, así que servidor y primer render del cliente pintan lo mismo.
  // Apenas la query resuelve gana `fetchedCourses`.
  const { courses: ssrCourses, authenticated: ssrAuthenticated } = useLoaderData<typeof loader>();
  const { data: fetchedCourses, isLoading: isLoadingCourses } = useAvailableCourses({
    language: language as SupportedLanguages,
  });
  const availableCourses = fetchedCourses ?? ssrCourses ?? undefined;

  // Fetch student courses (only if logged in)
  const { data: studentCourses } = useQuery(
    studentCoursesQueryOptions({ language }, { enabled: isLoggedIn }),
  );

  // Create sets of enrolled and purchased course IDs for quick lookup
  const enrolledCourseIds = useMemo(() => {
    if (!studentCourses) return new Set<string>();
    return new Set(studentCourses.filter((c) => c.enrolled).map((c) => c.id));
  }, [studentCourses]);

  const purchasedCourseIds = useMemo(() => {
    if (!studentCourses) return new Set<string>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Set(studentCourses.filter((c) => (c as any).purchased).map((c) => c.id));
  }, [studentCourses]);

  const allCourses = useMemo(() => defaultOrder(availableCourses ?? []), [availableCourses]);
  // Las facetas SIEMPRE sobre el set sin filtrar: si se derivaran del filtrado, el control
  // desaparecería justo cuando el usuario se metió en un rincón vacío y lo necesita para salir.
  const facets = useMemo(() => deriveFacets(availableCourses ?? []), [availableCourses]);

  /**
   * El estado CRUDO (`filters`) puede traer una categoría que no existe: la siembra
   * `searchParams.get("category")` y la URL la escribe cualquiera (un link viejo, una categoría
   * renombrada, una categoría con página pero sin cursos publicados). Todo lo que consume filtros de
   * acá para abajo usa la versión RESUELTA contra las facetas reales.
   *
   * Esto es el arreglo del bloqueante: antes, con un `?category=` que no matcheaba, el primer render
   * ya salía con la grilla vacía y el aviso de "ninguno coincide" empujando la primera tarjeta a
   * 469/496/537px contra un techo de 300, sin que el usuario tocara nada. Resolver por derivación
   * pura (y no en un `useEffect`) es lo que hace que ese estado no exista ni por un render, ni en el
   * servidor — donde arranca esta página y donde no hay efectos.
   *
   * Bonus de accesibilidad, no accidental: con la categoría resuelta el radiogroup siempre tiene UN
   * chip en `aria-checked=true` ("Todas las categorías" cuando el valor de la URL no sirve). Antes
   * los tres quedaban en `false` y el lector de pantalla anunciaba todo como "no seleccionado".
   */
  const effectiveFilters = useMemo<CourseFilters>(
    () => ({ ...filters, category: resolveCategory(filters.category, facets.categories) }),
    [filters, facets.categories],
  );

  const visible = useMemo(
    () => defaultOrder(applyFilters(availableCourses ?? [], effectiveFilters)),
    [availableCourses, effectiveFilters],
  );

  useEffect(() => {
    if (import.meta.env.DEV && (availableCourses?.length ?? 0) >= CLIENT_FILTER_MAX) {
      console.warn(
        `[/courses] ${availableCourses?.length} cursos publicados: el filtrado en cliente está al ` +
          `límite del perPage:100 de useAvailableCourses.ts:35 y a partir de acá miente (filtra ` +
          `sólo la página cargada). Esta página necesita paginación real, no un modo dual.`,
      );
    }
  }, [availableCourses?.length]);

  const handleFilterChange = useCallback(
    <K extends keyof CourseFilters>(name: K, value: CourseFilters[K]) => {
      switch (name) {
        case "searchQuery":
          dispatch({ type: "SET_SEARCH_QUERY", payload: value as string });
          break;
        case "category":
          dispatch({ type: "SET_CATEGORY", payload: value as string });
          break;
        case "priceFilter":
          dispatch({
            type: "SET_PRICE_FILTER",
            payload: value as CourseFilters["priceFilter"],
          });
          break;
      }
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    dispatch({ type: "CLEAR_FILTERS" });
  }, []);

  // `useOpenPreinscriptionCourses` NO entra acá a propósito: es una tercera query que resuelve
  // cuando quiere, y meterla en `isLoading` haría esperar a las tarjetas por datos de una sección que
  // vive 1500px más abajo. Lo que sí importa es que NADA del layout de arriba dependa de ella: por
  // eso el ancla a "Próximamente" se movió a un renglón de alto reservado (ver más abajo) y salió de
  // la barra de filtros, donde su aparición tardía empujaba la grilla 60px.
  // `&& !availableCourses`: con data del loader arrancamos con las tarjetas pintadas y las
  // queries refrescan por detrás. Sin data del loader (navegación cliente) se comporta igual
  // que antes.
  //
  // `|| ssrAuthenticated` es la excepción, y no es cosmética: la data del loader es ANÓNIMA,
  // así que el set de matriculados sale vacío y cada tarjeta muestra el botón del
  // deslogueado. Medido: ~330 ms en los que un alumno ve "Inscribirme" en un curso que ya
  // cursa y "Comprar" en uno que ya pagó. Si hay cookie de sesión esperamos al usuario —el
  // comportamiento de siempre—; si no la hay, el visitante ES anónimo y esos botones son los
  // correctos. El crawler nunca trae cookie, así que sigue recibiendo el catálogo entero.
  const isLoading = (isLoadingCourses || isLoadingUser) && (!availableCourses || ssrAuthenticated);

  // Los cursos de "Próximamente" NO están publicados: viven en otro endpoint
  // (preinscriptionOpen), así que el catálogo no los traía y quedaban invisibles acá.
  // Van en su propia sección, después de los disponibles: no se pueden comprar todavía,
  // mezclarlos con los que sí se compran (y con los filtros de precio) confundiría.
  const { data: upcoming } = useOpenPreinscriptionCourses();
  // `!!upcoming?.length` y no `upcomingCount > 0`: TS usa este alias para estrechar `upcoming` a
  // no-undefined dentro del `{hasUpcoming && …}` de la sección de Próximamente.
  const hasUpcoming = !!upcoming?.length;
  const upcomingCount = upcoming?.length ?? 0;

  const filtersActive = isFilterActive(effectiveFilters);
  const catalogEmpty = allCourses.length === 0;
  const noMatch = !catalogEmpty && visible.length === 0;
  // Si los filtros vaciaron la grilla, mostramos igual TODO el catálogo debajo del aviso: es una
  // vidriera, y si lo que pediste no está se muestra lo que hay. Pintar una pantalla vacía con un
  // ícono de libro es el callejón sin salida que tenía antes.
  const gridCourses = noMatch ? allCourses : visible;

  /**
   * ═══ UN solo nodo de live region, siempre el mismo elemento ═══
   *
   * Antes acá había un ternario que devolvía `<p role=status>` con filtros activos y
   * `<span class="sr-only" role=status>` sin ellos. Cambiar el TIPO de elemento hace que React
   * desmonte y monte el nodo, y una live region insertada JUNTO con su contenido no se anuncia de
   * forma confiable (NVDA y JAWS necesitan que la región exista antes del cambio). Medido con
   * MutationObserver: al aplicar el primer chip las mutaciones eran
   * `["REMOVED SPAN Mostrando 9 cursos", "ADDED P 3 de 9 cursos"]` — o sea el filtrado que estrena el
   * cambio de grilla era justo el que el lector NO anunciaba; del segundo en adelante sí.
   *
   * Ahora el `<p>` se renderiza SIEMPRE (también mientras carga y con el catálogo vacío): lo único
   * que cambia es el texto y la clase. `sr-only` es `position:absolute`, así que la variante invisible
   * no ocupa ni un píxel del renglón de 44.
   */
  const statusText = isLoading
    ? t("landing.courses.results.loading")
    : catalogEmpty
      ? t("landing.courses.results.emptyCatalogTitle")
      : noMatch
        ? t("landing.courses.results.noMatchLine", { total: allCourses.length })
        : filtersActive
          ? t("landing.courses.results.filtered", {
              shown: visible.length,
              total: allCourses.length,
            })
          : visible.length === 1
            ? t("landing.courses.results.showingOne")
            : t("landing.courses.results.showing", { count: visible.length });
  // Visible sólo con filtros activos: ahí sí importa saber que estás viendo un subconjunto. La línea
  // permanente "Mostrando 2 cursos" se fue del render visual (eran 20px fijos para informar algo que
  // se cuenta a ojo) pero sigue en la live region para el lector de pantalla.
  const statusVisible = !isLoading && !catalogEmpty && filtersActive;

  // Cantidad de skeletons: la del render anterior, con techo de 3. Antes eran 8 fijos mientras
  // cargaban 2 cursos, o sea el loading prometía 4× el catálogo y después colapsaba.
  const lastCount = useRef(2);
  useEffect(() => {
    if (!isLoading && visible.length > 0) lastCount.current = visible.length;
  }, [isLoading, visible.length]);
  const skeletonCount = Math.min(lastCount.current || 2, 3);

  const renderCard = (course: (typeof gridCourses)[number], index: number) => {
    const courseAny = course as Record<string, unknown>;
    return (
      <li key={course.id} className="flex">
        <PublicCourseCard
          course={course}
          isEnrolled={enrolledCourseIds.has(course.id) || !!courseAny.enrolled}
          isPurchased={purchasedCourseIds.has(course.id) || !!courseAny.purchased}
          isPreinscribed={!!courseAny.preinscribed}
          isLoggedIn={isLoggedIn}
          priority={index < 2}
        />
      </li>
    );
  };

  return (
    // `pt-6` (24px) y `sm:pt-10` (40px) son desviaciones CONSCIENTES: §10.11 del manual pide 48px de
    // padding de sección en mobile y 40 no está en la escala de 4/8/16/24/32/48. Se dejan porque cada
    // píxel de acá sale del fold, que es el problema que esta página vino a resolver. Anotado para el
    // Decisions Log de DESIGN.md.
    <section className="pb-16 pt-6 sm:pb-20 sm:pt-10 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Hero — izquierda, no centrado: el centrado con `max-w-2xl mx-auto` empujaba el subtítulo
            a 3 líneas en 390 y a 4 en 360, justo en la pantalla más chica. */}
        <header className="max-w-[720px]">
          {/* 28 / 36 / 48 son display-md mobile, display-lg mobile y display-lg desktop de §2.4. El
              `lg:text-[44px]` que había no existe en la escala tipográfica. */}
          <h1 className="font-display text-[28px] font-bold leading-[1.15] tracking-tight text-landing-text sm:text-[36px] lg:text-[48px]">
            {t("landing.courses.title")}
          </h1>
          {/* Las dos versiones del subtítulo van en el DOM, alternadas por CSS. Duplicar el texto es
              el precio elegido a conciencia: con `{isMobile && …}` el HTML del servidor perdería
              texto rastreable y volveríamos a los fantasmas de hidratación ya documentados. */}
          <p className="landing-meta mt-2 uppercase tracking-wide text-landing-muted sm:hidden">
            {t("landing.courses.heroMeta")}
          </p>
          <p className="mt-4 hidden text-[18px] leading-relaxed text-landing-muted sm:block lg:text-[20px]">
            {t("landing.courses.description")}
          </p>
        </header>

        {/* Barra de filtros. Mientras carga se RESERVA su alto (44px + el mt-4) en vez de dejar el
            hueco en cero: la barra se pinta recién cuando llegan los datos, así que sin la reserva la
            grilla nacía 60px más arriba y bajaba de golpe al resolver la query — con "Slow 3G" el
            salto se ve a ojo, y las mediciones nunca lo agarraron porque se tomaban después de
            `networkidle`.
            DEUDA CONOCIDA, escrita como deuda Y MEDIDA: si el catálogo resulta no tener ningún
            control que ofrecer, la barra devuelve `null`, la reserva se libera y la grilla sube 60px
            una sola vez (44 de la barra + 16 del `mt-4`). El número no es una estimación: está en
            `/tmp/qa-courses-redesign/facets-report.json` → `cls.stagingReal`, que da CLS 0.0511 con
            las fuentes `UL.mt-2 grid… 287→227` y `DIV.mt-4 flex min-h-11… 235→175`. Pasa en los dos
            escenarios donde la barra no tiene nada que ofrecer: `stagingReal` (2 cursos, los 2
            gratis, 1 categoría) y `enrolledStates` (2 cursos, los 2 pagos → tampoco hay chips de
            precio, porque falta la población gratis). Se elige ese salto único antes que dejar 60px
            de blanco permanente en la primera pantalla, y desaparece en cuanto el catálogo tiene un
            gratis Y un pago — el de prod ya lo tiene (`cls.prodMixedPrice` = 0.0001). */}
        {isLoading ? (
          <div data-qa="filter-bar-reserved" aria-hidden="true" className="mt-4 h-11 sm:mt-6" />
        ) : (
          <CoursesFilterBar
            className="mt-4 sm:mt-6"
            filters={effectiveFilters}
            facets={facets}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        )}

        {/* ═══ Renglón de servicio ═══
            Se renderiza SIEMPRE, desde el primer render, con alto fijo. Antes existía sólo con
            `!isLoading && !catalogEmpty`, así que aparecía junto con las tarjetas y sumaba otros
            ~24px de empujón.
            Lleva dos cosas:
            - UNA live region (`role="status"`), que es el conteo cuando hay filtros activos y el
              aviso de "ninguno coincide" cuando los filtros vaciaron la grilla. Al filtrar en cliente
              es la única pista de que la grilla cambió (no hay estado de carga que la dé);
            - el ancla a "Próximamente", que se mudó desde la barra de filtros. Va acá porque NO es un
              filtro (los cursos con preinscripción abierta viven en otro endpoint y ningún filtro los
              toca) y porque este renglón ya existía: mudarla no le suma ni un píxel al fold, mientras
              que dentro de la barra era la causa directa del salto de 44 a 96px.
            El renglón mide 44px y no 24 porque el ancla es un target táctil y 44 es el piso. Es el
            único costo de la mudanza, y el que paga la estabilidad: aparezca o no el ancla cuando
            resuelva su query, el alto es el mismo.

            ═══ Por qué el aviso de "ninguno coincide" vive ACÁ y no arriba de la grilla ═══
            Antes era un bloque propio (`NoMatchNotice`: título 18px + cuerpo + botón, 182px de alto
            con borde y fondo) insertado ENTRE el renglón y la grilla. Empujaba la primera tarjeta 182px
            hacia abajo — y como el filtro se sembraba desde la URL, eso pasaba en el primer paint.
            De las tres salidas posibles (que quepa en este renglón de 44px, que vaya DEBAJO de la
            grilla, o que sea una línea) se eligió la LÍNEA en el renglón que ya existe, por tres
            razones: (1) es literalmente un aviso de servicio, del mismo rango que "3 de 9 cursos", y
            este renglón es el renglón de servicio; (2) cuesta CERO píxeles, porque el renglón se
            renderiza igual con alto fijo desde el primer render — debajo de la grilla habría costado
            un bloque nuevo y encima habría seguido empujando; (3) al ser el mismo nodo que el conteo,
            el cambio "3 de 9" → "ninguno coincide" lo anuncia el lector de pantalla sin remontar nada.
            La SALIDA del estado no está en esta línea: es el botón "Limpiar filtros", que en la barra
            de filtros pasó a ser el PRIMER ítem del riel justamente para que se vea sin scrollear
            (antes era el último, o sea la única salida del "0 de 2 cursos" quedaba debajo del degradé
            o fuera de pantalla). Y la grilla de abajo sigue mostrando el catálogo completo. */}
        <div data-qa="service-row" className="mt-4 flex min-h-11 items-center gap-3">
          <p
            data-qa="results-status"
            role="status"
            aria-live="polite"
            className={cn(
              statusVisible ? "landing-meta line-clamp-2 min-w-0 text-landing-muted" : "sr-only",
            )}
          >
            {statusText}
          </p>

          {/* El eyebrow "EMPEZÁ ACÁ" que estaba acá se eliminó, no se movió: prometía que la primera
              tarjeta es el curso de entrada del embudo, y no hay ningún campo en la base que diga cuál
              es (ver `defaultOrder` en lib/courseFacets.ts). Un rótulo que no se puede garantizar es
              peor que no tener rótulo. */}
          {hasUpcoming && (
            // Enlace de texto, no botón: es navegación interna, no una acción. Lo que sí tiene es
            // `min-h-11`, o sea 44px de área táctil dentro de un renglón de 44px, aunque el texto mida
            // 20.
            <a
              href="#proximamente"
              className="ml-auto inline-flex min-h-11 shrink-0 items-center gap-1.5 text-[14px] font-semibold text-landing-stone-700 underline decoration-landing-stone-300 decoration-1 underline-offset-4 transition-colors duration-200 ease-out hover:text-landing-red hover:decoration-landing-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-red focus-visible:ring-offset-2 focus-visible:ring-offset-landing-cream"
            >
              {t("landing.courses.upcomingAnchor", { count: upcomingCount })}
              <ArrowDown aria-hidden="true" className="size-4" />
            </a>
          )}
        </div>

        {/* Course Grid */}
        {isLoading ? (
          // `mt-2`, el MISMO offset que la grilla real. Antes era `mt-6`: la promesa de "misma
          // geometría" valía para la tarjeta pero no para los márgenes del contenedor, así que al
          // llegar los datos la grilla se movía 16px sola.
          <ul className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <li key={i} className="flex">
                <CourseCardSkeleton />
              </li>
            ))}
          </ul>
        ) : catalogEmpty ? (
          <EmptyCatalogState hasUpcoming={hasUpcoming} />
        ) : (
          /* `xl:grid-cols-4` se eliminó: con ≤9 cursos publicados dejaba dos columnas vacías y
             tarjetas de 286px en un contenedor de 1216. Volver a ponerlo recién a partir de 10.
             El aviso de "ninguno coincide" YA NO se inserta acá arriba: es una línea en el renglón de
             servicio (ver el comentario largo más arriba). Lo que sí sigue igual es que la grilla
             muestre el catálogo completo (`gridCourses`) cuando los filtros no matchean nada. */
          <ul className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {gridCourses.map(renderCard)}
          </ul>
        )}

        {/* ═══ Próximamente: reservás gratis y te asegurás el 80% ═══ */}
        {/* El `id` lo necesita el ancla de la barra de filtros; el `scroll-margin-top: 96px` que
            evita que el header sticky tape el título ya existe en index.css para este id. */}
        {hasUpcoming && (
          <div id="proximamente" className="mt-16 border-t border-landing-border pt-12">
            <div className="text-center">
              <span className="landing-eyebrow block font-bold text-landing-teal">
                {t("landing.launch.next.eyebrow")}
              </span>
              <h2 className="font-display mt-2 text-[clamp(22px,2.8vw,30px)] font-bold tracking-tight text-landing-text">
                {t("landing.launch.next.title")}
              </h2>
              <p className="mx-auto mt-2 max-w-[52ch] text-[15px] text-landing-muted">
                {t("landing.launch.next.discountHeadline")}
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {upcoming.map((course) => (
                <article
                  key={course.id}
                  className="flex flex-col rounded-xl border border-landing-border bg-landing-paper p-4 shadow-landing-sm"
                >
                  <span className="landing-eyebrow inline-flex w-max items-center gap-1.5 text-landing-red">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2 w-2 rounded-full bg-landing-red"
                    />
                    {t("landing.launch.next.prepStamp")}
                  </span>
                  {/* `font-semibold` y no `font-bold`: es el mismo nivel jerárquico que el h3 de la
                  tarjeta del catálogo (§10.12 pide Fraunces 600) y tenerlos en dos pesos distintos
                  hacía leer dos jerarquías donde hay una. */}
                  <h3 className="font-display mt-2 text-[18px] font-semibold leading-tight text-landing-text">
                    {course.title}
                  </h3>
                  {course.description && (
                    <p className="mt-1 line-clamp-2 text-[13.5px] leading-snug text-landing-muted">
                      {leadSentences(course.description, 110)}
                    </p>
                  )}
                  <p className="mt-3 text-[13.5px] font-bold text-landing-text">
                    {t("landing.launch.next.freeLock")}
                  </p>
                  <div className="mt-auto flex flex-col gap-2 pt-4">
                    {course.isPreinscribed ? (
                      <span className="flex w-full items-center justify-center gap-2 rounded-lg border border-landing-success bg-landing-success-light px-4 py-3 text-[14px] font-bold text-landing-success">
                        ✓ {t("landing.launch.next.reserved")}
                      </span>
                    ) : (
                      <>
                        <Link
                          to={`/preinscripcion/${course.id}`}
                          data-cuelume-press
                          data-cuelume-release
                          className="flex w-full items-center justify-center gap-2 rounded-lg bg-landing-red px-4 py-3 text-[14px] font-bold text-white shadow-landing-red transition-all duration-200 hover:-translate-y-0.5 hover:bg-landing-red-hover"
                        >
                          🔒 {t("landing.launch.next.reserveCta")}
                        </Link>
                        <ReserveInCartButton course={course} />
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Skeleton con la MISMA geometría que la tarjeta final. Antes tenía otra forma (y otra cantidad),
 * así que al llegar los datos la página saltaba.
 */
function CourseCardSkeleton() {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-landing-border bg-landing-white">
      {/* Cada `<Skeleton>` necesita el color por instancia: el primitivo es `bg-neutral-100`, que en
          oscuro da #131320 azulado, y no se edita porque lo usa el admin. */}
      <Skeleton className="aspect-video w-full bg-landing-stone-200 motion-reduce:animate-none" />
      <div className="flex flex-1 flex-col p-4">
        <Skeleton className="h-4 w-24 bg-landing-stone-200 motion-reduce:animate-none" />
        {/* `h-12` = 48px, el mismo `min-h-12` que el h3 de la tarjeta real. */}
        <Skeleton className="mt-2 h-12 w-full bg-landing-stone-200 motion-reduce:animate-none" />
      </div>
      <div className="flex items-center gap-2 border-t border-landing-border px-4 py-3">
        <Skeleton className="h-10 w-20 bg-landing-stone-200 motion-reduce:animate-none" />
        <Skeleton className="h-11 flex-1 bg-landing-stone-200 motion-reduce:animate-none" />
      </div>
    </div>
  );
}

const emptyIcon = (
  <svg
    className="size-12 text-landing-stone-400 opacity-50"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

/**
 * Catálogo VACÍO (sin filtros aplicados). Esta rama antes no existía: si se despublicaran los
 * cursos, la página decía "Intenta ajustar los filtros de búsqueda" sin filtros aplicados.
 */
function EmptyCatalogState({ hasUpcoming }: { hasUpcoming: boolean }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 rounded-full bg-landing-stone-100 p-4" aria-hidden="true">
        {emptyIcon}
      </div>
      {/* Sin `font-display`: §10.9 es el único lugar del manual donde un h3 va en DM Sans 600 en vez
          de Fraunces. Es el estado vacío, no un título de contenido. */}
      <h3 className="text-[18px] font-semibold text-landing-stone-700">
        {t("landing.courses.results.emptyCatalogTitle")}
      </h3>
      <p className="mt-2 max-w-[400px] text-[15px] text-landing-muted">
        {t("landing.courses.results.emptyCatalogBody")}
      </p>
      {hasUpcoming && (
        <Button
          variant="brand-outline"
          size="lg"
          className="mt-6 bg-landing-white focus-visible:ring-landing-red focus-visible:ring-offset-landing-cream"
          asChild
        >
          <a href="#proximamente">{t("landing.courses.results.emptyCatalogCta")}</a>
        </Button>
      )}
    </div>
  );
}
